import { z } from "zod";
import { Action, Subject } from "@erp/authz";
import { SystemEvent, SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";
import { SystemEntitySchema } from "../../utils/system-entities";
import { taskOutputSchema } from "./shared";

/**
 * Resolve Task Step
 *
 * The primary attachment mechanism between workflows and the durable task
 * spine: idempotently resolve THE task for a unit of business work. For a
 * task type + anchor entity it returns the existing OPEN task for
 * (organization, type, anchor) or creates one — converging no matter which
 * workflow fires first. An explicit taskId is supported as an alternative
 * input for non-anchored usage.
 */
export const resolveTaskStep = defineStepConfig({
  type: "resolve_task",
  name: "Resolve Task",
  description:
    "Idempotently resolve the durable task for a business entity: return the existing open task for (task type, anchor entity) or create one. Alternatively, pass an explicit task id.",
  category: "Tasks",

  inputSchema: z.object({
    taskId: z
      .uuid()
      .optional()
      .describe(
        "Explicit task id to resolve — alternative to type + anchor for non-anchored usage. When set, the type/anchor inputs are ignored.",
      ),
    taskTypeKey: z
      .string()
      .optional()
      .describe(
        "Org-scoped task type key (e.g. 'approve-invoice'). Wire from an upstream step; overrides the config default.",
      ),
    anchorEntity: SystemEntitySchema.optional().describe(
      "The business entity the task is anchored to. The entity type must match the task type's declared anchorEntityType.",
    ),
    anchorEntityType: z
      .nativeEnum(SystemEntity)
      .optional()
      .describe(
        "Anchor entity type — an alternative to `anchorEntity` for when you only have a bare entity id (paired with `anchorEntityId`). Ignored if `anchorEntity` is provided.",
      ),
    anchorEntityId: z
      .uuid()
      .optional()
      .describe(
        "Anchor entity id — an alternative to `anchorEntity` (paired with `anchorEntityType`). Ignored if `anchorEntity` is provided.",
      ),
    title: z
      .string()
      .optional()
      .describe(
        "Title for a newly created task (defaults to the task type's name). Ignored when an existing task is resolved.",
      ),
    description: z
      .string()
      .optional()
      .describe("Description for a newly created task"),
    priority: z
      .enum(["low", "medium", "high", "urgent"])
      .optional()
      .describe("Priority for a newly created task (defaults to medium)"),
    metadata: z
      .record(z.string(), z.unknown())
      .optional()
      .describe("Metadata to attach to a newly created task"),
    tags: z
      .array(z.string())
      .optional()
      .describe("Tags for a newly created task"),
    parentTaskId: z
      .uuid()
      .optional()
      .describe(
        "Parent task a newly created task is attached under — used to idempotently resolve an anchored stage subtask (e.g. 'the extraction subtask for this invoice'). An existing open task is returned as-is, never re-parented.",
      ),
    taskNamespaceId: z
      .uuid()
      .optional()
      .describe(
        "Namespace to create the task in when resolving creates one. Overrides the step's configured namespace. A selected task type with a live default namespace still wins; otherwise the org default is used.",
      ),
  }),

  outputSchema: z.object({
    task: taskOutputSchema,
    taskId: z.uuid().describe("The resolved task's id (flat, for wiring)"),
    created: z
      .boolean()
      .describe(
        "True when a new task was created, false when an existing open task was resolved",
      ),
  }),

  configSchema: z.object({
    taskTypeKey: z
      .string()
      .trim()
      .min(1)
      .optional()
      .describe(
        "Default org-scoped task type key used when the `taskTypeKey` input is not wired.",
      ),
    reuseClosedTask: z
      .boolean()
      .optional()
      .describe(
        "When no open task holds the (type, anchor) slot, resolve the most recently closed one instead of creating a fresh task. For stage subtasks that conclude once per entity, so workflow reruns don't accrete duplicates.",
      ),
    taskNamespaceKey: z
      .string()
      .trim()
      .min(1)
      .optional()
      .describe(
        "Namespace key (e.g. 'SHIP') used when creating a new task. Portable across orgs — resolved at run time. Used when the `taskNamespaceId` input is not wired. A selected task type with a live default namespace still wins.",
      ),
    taskNamespaceId: z
      .uuid()
      .optional()
      .describe(
        "Namespace id used when creating a new task. Prefer `taskNamespaceKey` in templates. The input socket overrides this field. A selected task type with a live default namespace still wins.",
      ),
  }),

  requiredPermissions: [{ action: Action.Create, subject: Subject.Task }],

  // Emitted indirectly: TaskService publishes TASK_CREATED when the resolve
  // creates a new task (the single lifecycle emission point).
  emits: [SystemEvent.TASK_CREATED] as const,
});
